type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE"

function getCsrfToken(): string | undefined {
  const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
  return meta?.content
}

interface ApiOptions {
  method?: HttpMethod
  body?: BodyInit | null
  headers?: Record<string, string>
}

/**
 * Fetch helper for same-origin API calls.
 * Auth is cookie-based (HttpOnly zim_token) — always send credentials.
 * Do not scrape document.cookie for JWT; JS cannot read HttpOnly cookies.
 */
export async function apiFetch<T = any>(url: string, options: ApiOptions = {}): Promise<T> {
  const csrf = getCsrfToken()

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  }

  // FormData must set its own multipart boundary — never force application/json
  if (typeof FormData !== "undefined" && options.body instanceof FormData) {
    delete headers["Content-Type"]
    delete headers["content-type"]
  }

  if (csrf && options.method && options.method !== "GET") {
    headers["X-CSRF-Token"] = csrf
  }

  const res = await fetch(url, {
    method: options.method || "GET",
    headers,
    body: options.body,
    credentials: "same-origin",
  })

  if (!res.ok) {
    const text = await res.text().catch(() => "Request failed")
    let message = text
    try {
      const json = JSON.parse(text)
      message = json.error || json.message || text
    } catch {
      // not JSON
    }
    throw new ApiError(message, res.status)
  }

  const contentType = res.headers.get("content-type") || ""
  if (contentType.includes("application/json")) {
    return res.json()
  }
  return res.text() as unknown as T
}

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
    this.name = "ApiError"
  }
}

export async function apiPost<T = any>(url: string, body: BodyInit | null, headers?: Record<string, string>): Promise<T> {
  return apiFetch<T>(url, { method: "POST", body, headers })
}

export async function apiPatch<T = any>(url: string, body: BodyInit | null, headers?: Record<string, string>): Promise<T> {
  return apiFetch<T>(url, { method: "PATCH", body, headers })
}

export async function apiDelete<T = any>(url: string, headers?: Record<string, string>): Promise<T> {
  return apiFetch<T>(url, { method: "DELETE", headers })
}

export async function apiGet<T = any>(url: string, headers?: Record<string, string>): Promise<T> {
  return apiFetch<T>(url, { method: "GET", headers })
}
